import { getApiClient } from './client'
import type {
  MoveResponseDto,
  MoveListResponseDto,
  CreateMoveDto,
  UpdateMoveDto,
} from './generated/api'

/**
 * Moves API Facade
 *
 * Type-safe wrapper around the generated API client for moves operations.
 * Provides consistent error handling and cleaner API for React Query hooks.
 */

/**
 * Fetch all moves with optional filters
 *
 * @param params - Query parameters for filtering
 * @param params.searchText - Search moves by name or description
 * @param params.limit - Maximum number of results (default: 20)
 * @param params.offset - Number of results to skip (default: 0)
 * @param params.includeRelations - Include related entities (series, tags)
 * @returns Promise resolving to list of moves with pagination metadata
 */
export async function fetchMoves(params?: {
  searchText?: string
  limit?: number
  offset?: number
  includeRelations?: boolean
}): Promise<MoveListResponseDto> {
  const response = await getApiClient().api.movesControllerFindAll({
    searchText: params?.searchText,
    limit: params?.limit,
    offset: params?.offset,
    includeRelations: params?.includeRelations,
  })

  return response.data
}

/**
 * Fetch a single move by ID
 *
 * @param id - Move ID (UUID)
 * @param includeRelations - Include related entities (series, tags)
 * @returns Promise resolving to move details
 */
export async function fetchMove(
  id: string,
  includeRelations?: boolean
): Promise<MoveResponseDto> {
  const response = await getApiClient().api.movesControllerFindById(id, {
    includeRelations,
  })

  return response.data
}

/**
 * Create a new move
 *
 * @param data - Move data to create
 * @returns Promise resolving to created move
 */
export async function createMove(
  data: CreateMoveDto
): Promise<MoveResponseDto> {
  const response = await getApiClient().api.movesControllerCreate(data)

  return response.data
}

/**
 * Update an existing move
 *
 * @param id - Move ID (UUID)
 * @param data - Partial move data to update
 * @returns Promise resolving to updated move
 */
export async function updateMove(
  id: string,
  data: UpdateMoveDto
): Promise<MoveResponseDto> {
  const response = await getApiClient().api.movesControllerUpdate(id, data)

  return response.data
}

/**
 * Delete a move (soft delete)
 *
 * @param id - Move ID (UUID)
 * @returns Promise resolving to void on success
 */
export async function deleteMove(id: string): Promise<void> {
  await getApiClient().api.movesControllerSoftDelete(id)
}
